<template>
    <div
        v-if="!hasStartedInlineCheckout || isCreatingCheckout || isLoading"
        class="mx-auto mt-12 max-w-screen-xl"
    >
        <prime-skeleton />
    </div>

    <checkout-checking-payment
        v-else-if="checkoutStatus === 'ARMED'"
        :checkout-id="checkoutId"
        kiosk-completion
        @kiosk-checkout-success="handleCheckoutSuccess"
        @checkout-status-changed="handleCheckoutStatusChanged"
    />

    <checkout-processing-payment
        v-else-if="checkoutStatus === 'PROCESSING'"
        :checkout-id="checkoutId"
        kiosk-completion
        @kiosk-checkout-success="handleCheckoutSuccess"
        @checkout-status-changed="handleCheckoutStatusChanged"
    />

    <checkout-payment-recovery-error v-else-if="checkoutStatus === 'ERROR'" />

    <div
        v-else-if="completedCheckout !== undefined"
        class="mx-auto mt-12 max-w-screen-xl"
    >
        <!-- The completion watcher owns kiosk navigation; keep this transition visually stable. -->
        <prime-skeleton />
    </div>

    <div v-else>
        <app-error v-if="data === undefined" />
        <app-error v-else-if="checkout === undefined" />
        <not-found v-else-if="checkout === null" />
        <purchase-contract-section v-else>
            <template #title>{{ t('cartSidebar.checkout') }}</template>
            <div class="grid grid-cols-1">
                <div>
                    <div>
                        <nuxt-error-boundary @error="logError">
                            <checkout-form
                                :query="data"
                                :checkout="checkout"
                                :contract="contract"
                                :balance-due-mode="balanceDueMode"
                                @kiosk-checkout-success="handleCheckoutSuccess"
                                @checkout-status-changed="
                                    handleCheckoutStatusChanged
                                "
                            />

                            <template #error>
                                <app-error />
                            </template>
                        </nuxt-error-boundary>
                    </div>
                </div>

                <div class="order-first bg-gray-50">
                    <div class="max-w-[650px]">
                        <checkout-cart
                            :checkout="checkout"
                            :balance-due-mode="balanceDueMode"
                        />
                    </div>
                </div>
            </div>
        </purchase-contract-section>
    </div>
</template>

<script setup lang="ts">
import { useSelectedLanguage } from '~/composables/use-selected-language'
import { useActiveFacility, useKioskSuccess, useStorefront } from '#imports'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData, graphql } from '~/resources/graphql'
import { computed, onMounted, ref, watch } from 'vue'
import PrimeSkeleton from 'primevue/skeleton'
import { useRpQuery } from '~/composables/graphql'
import CheckoutCart from '~/components/Checkout/Cart/CheckoutCart.vue'
import CheckoutCheckingPayment from '~/components/Checkout/CheckoutCheckingPayment.vue'
import CheckoutForm from '~/components/Checkout/CheckoutForm.vue'
import CheckoutPaymentRecoveryError from '~/components/Checkout/CheckoutPaymentRecoveryError.vue'
import CheckoutProcessingPayment from '~/components/Checkout/CheckoutProcessingPayment.vue'
import NuxtErrorBoundary from '#app/components/nuxt-error-boundary'
import AppError from '~/components/NotFound/AppError.vue'
import { useLogError } from '~/composables/use-log-error'
import { KioskCheckoutResultFragment } from '~/composables/use-kiosk-success'
import NotFound from '~/components/NotFound/NotFound.vue'
import { provideCheckoutPaymentData } from '~/components/Checkout/Sections/Payment/checkout-payment-data'
import { DocumentSectionContractFragment } from '~/components/PurchaseContract/DocumentSection/DocumentSection.vue'
import PurchaseContractSection from '~/components/PurchaseContract/PurchaseContractSection.vue'
import { useI18n } from 'vue-i18n'
import { CheckoutFormCheckoutFragment } from '~/components/Checkout/Forms/use-checkout-form'
import { Decimal } from '~/composables/support/decimal'

const props = defineProps<{
    contract: FragmentType<typeof DocumentSectionContractFragment>
}>()

const { t } = useI18n({ useScope: 'global' })
const activeFacility = useActiveFacility()
const query = useRpQuery()

// Parent purchase screens mount this section only in kiosk mode.
const logError = useLogError()

// The contract remains available to the embedded checkout form after creation.
const contractId = computed(() => props.contract.id)
const selectedLanguage = useSelectedLanguage()
const checkoutId = ref('')
const hasStartedInlineCheckout = ref(false)
const queryClient = useQueryClient()
const { mutate: createInlineCheckout, isPending: isCreatingCheckout } =
    useMutation({
        mutationFn: async () => {
            const response = await query(
                graphql(/* GraphQL */ `
                    mutation CreateInlineCheckout($contractId: ID!) {
                        createInlineCheckout(contractId: $contractId) {
                            recordId
                        }
                    }
                `),
                { contractId: contractId.value }
            )

            return response.createInlineCheckout.recordId
        },
        onSuccess(createdCheckoutId) {
            // Draft checkout identities are reused, so discard any quote cached before contract pricing changed.
            // Enabling the query only after removal keeps payment fields hidden until a fresh response arrives.
            queryClient.removeQueries({
                queryKey: ['InlineCheckoutQuery', createdCheckoutId]
            })
            checkoutId.value = createdCheckoutId
        }
    })

const { data, isLoading, refetch } = useQuery({
    queryKey: [
        'InlineCheckoutQuery',
        checkoutId,
        activeFacility,
        selectedLanguage
    ],
    enabled: () => checkoutId.value !== '',
    // Kiosk and inline hosted payments keep resolving after preflight changes the checkout
    // status, so only their explicit recovery paths may replace the active checkout form.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: () =>
        query(
            graphql(/* GraphQL */ `
                query InlineCheckoutQuery(
                    $facilityId: ID!
                    $checkoutId: ID!
                    $language: Language!
                ) {
                    ...CheckoutFormQueryFragment
                    checkout(id: $checkoutId) {
                        __typename
                        ... on Checkout {
                            status
                            ...CheckoutFormCheckoutFragment
                            ...CheckoutCartFragment
                        }
                        ... on CommitCheckoutResult {
                            ...KioskCheckoutResultFragment
                        }
                    }
                }
            `),
            {
                facilityId: activeFacility.value.id,
                checkoutId: checkoutId.value,
                language: selectedLanguage.value
            }
        )
})

onMounted(() => {
    // Creating a checkout is a browser action, never a server-rendered query side effect.
    hasStartedInlineCheckout.value = true
    createInlineCheckout()
})

const checkout = computed(() => {
    if (data.value === undefined) {
        return undefined
    } else if (data.value.checkout === null) {
        return null
    } else {
        return data.value.checkout.__typename === 'Checkout'
            ? data.value.checkout
            : null
    }
})

const checkoutStatus = computed(() => {
    return data.value?.checkout?.__typename === 'Checkout'
        ? data.value.checkout.status
        : undefined
})

const completedCheckout = computed(() => {
    return data.value?.checkout?.__typename === 'CommitCheckoutResult'
        ? data.value.checkout
        : undefined
})

provideCheckoutPaymentData()

const goToSuccess = useKioskSuccess()

function handleCheckoutSuccess(
    data: FragmentType<typeof KioskCheckoutResultFragment>
) {
    const result = getFragmentData(KioskCheckoutResultFragment, data)
    goToSuccess({
        contracts: result.contracts.flatMap(contract => {
            return contract.__typename === 'PlanContract' ? [contract] : []
        }),
        invoice: result.invoice,
        analytics: balanceDueMode.value ? undefined : result
    })
}

async function handleCheckoutStatusChanged(): Promise<void> {
    await refetch()
}

const storefront = useStorefront()
const balanceDueMode = computed<boolean>(() => {
    if (storefront.value.collectMoney || !checkout.value) {
        return false
    }

    const checkoutData = getFragmentData(
        CheckoutFormCheckoutFragment,
        checkout.value
    )
    return new Decimal(checkoutData.dueToday).isNotZero()
})

const hasHandledCompletedCheckout = ref(false)
watch(
    completedCheckout,
    result => {
        if (result === undefined || hasHandledCompletedCheckout.value) {
            return
        }

        hasHandledCompletedCheckout.value = true
        handleCheckoutSuccess(result)
    },
    { immediate: true }
)
</script>
